import { useEffect } from 'react';

import { chatService, type ChatMessage } from '../../services/chatService';
import { eventService } from '../../services/eventService';
import { PLANNING_ACTIONS_REFRESH_EVENT } from '../planningActionsRefresh';

import type { ChatState } from './types';

/** Recover missed gates without replacing optimistic messages or newer SSE state. */
export function mergeRecoveredPlanningMessages(
  current: ChatMessage[], recovered: ChatMessage[],
): ChatMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]));

  recovered.forEach((message) => {
    const existing = byId.get(message.id);

    byId.set(message.id, existing ? {
      ...message,
      ...existing,
      phase: existing.phase ?? message.phase,
      buttons: existing.buttons ?? message.buttons,
      allowed_buttons: existing.allowed_buttons ?? message.allowed_buttons,
    } : message);
  });

  return Array.from(byId.values()).sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/** The session's latest messages, as the server has them. */
const sessionMessages = async (projectId: string, sessionId: string): Promise<ChatMessage[]> => {
  const response = await chatService.getMessages(50, 0, { projectId, sessionId });

  return response.messages.filter((message) => (
    message.project_id === projectId && (!message.session_id || message.session_id === sessionId)
  ));
};

/** An event for another project or session leaves this timeline alone. */
const concernsSession = (data: unknown, projectId: string, sessionId: string): boolean => {
  const payload = data as { project_id?: string; session_id?: string };

  return (!payload.project_id || payload.project_id === projectId)
    && (!payload.session_id || payload.session_id === sessionId);
};

/** Every sign a gate may be missing: a finished run, a reconnect, or a press refused as out of date. */
const subscribeRecovery = (recover: (data: unknown) => Promise<void>): (() => void) => {
  const onActionsRefresh = (event: Event) => {
    void recover({ project_id: (event as globalThis.CustomEvent<{ projectId?: string }>).detail?.projectId });
  };
  const unsubscribers = [
    eventService.subscribe('planning_complete', recover),
    eventService.subscribe('connected', recover),
  ];

  window.addEventListener(PLANNING_ACTIONS_REFRESH_EVENT, onActionsRefresh);

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    window.removeEventListener(PLANNING_ACTIONS_REFRESH_EVENT, onActionsRefresh);
  };
};

/** Runtime snapshots do not contain chat action gates; reconcile them separately. */
export function usePlanningMessageRecovery(
  projectId: string | null,
  sessionId: string | null,
  setMessages: ChatState['setMessages'],
): void {
  useEffect(() => {
    if (!projectId || !sessionId) return undefined;
    let disposed = false;
    let requested = 0;
    const recover = async (data: unknown) => {
      if (!concernsSession(data, projectId, sessionId)) return;
      const request = ++requested;

      try {
        const messages = await sessionMessages(projectId, sessionId);

        if (!disposed && request === requested) {
          setMessages((current) => mergeRecoveredPlanningMessages(current, messages));
        }
      } catch (error) {
        // Keep the live timeline intact; the next completion/reconnect can retry.
        console.warn('Could not synchronize planning action messages', error);
      }
    };
    const unsubscribe = subscribeRecovery(recover);

    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [projectId, sessionId, setMessages]);
}
